import React, { useState } from "react";
import { CheckCircle2, ChevronDown, ChevronUp, Clock3, XCircle } from "lucide-react";
import generateAvatarUrl from "../../utils/generateAvatarUrl";
import {
  computePotentialFromRecent,
  formatDrawCoin,
  formatDrawPlayedAt,
  getDrawResultDisplay,
} from "../../utils/bondDrawHelpers";

const DrawStatusPill = ({ status }) => {
  const normalized = String(status || "").toLowerCase();
  let tone = "lost";
  if (normalized === "won") tone = "won";
  else if (normalized === "refunded") tone = "refunded";
  else if (normalized === "pending") tone = "pending";

  return (
    <span className={`bdraw-player-status ${tone}`}>
      {(status || "—").toUpperCase()}
    </span>
  );
};

const DrawGameMetrics = ({ recent }) => {
  const potential = computePotentialFromRecent(recent);
  const result = getDrawResultDisplay(recent);

  return (
    <div className="bdraw-player-metrics">
      <div className="bdraw-player-metric">
        <span className="bdraw-player-metric-label">Stake</span>
        <strong>{formatDrawCoin(recent.stake)}</strong>
      </div>
      <div className="bdraw-player-metric">
        <span className="bdraw-player-metric-label">Max Win</span>
        <strong className="warn">{formatDrawCoin(potential)}</strong>
      </div>
      <div className="bdraw-player-metric">
        <span className="bdraw-player-metric-label">Result</span>
        <strong className={result.tone}>{result.text}</strong>
      </div>
    </div>
  );
};

const DrawNumberPills = ({ numbers = [], match = false }) => {
  if (!numbers.length) return <span className="bdraw-player-meta">—</span>;
  return (
    <span className="bdraw-player-pills">
      {numbers.map((n, idx) => (
        <span key={`${n}-${idx}`} className={`bdraw-player-pill${match ? " match" : ""}`}>
          {n}
        </span>
      ))}
    </span>
  );
};

const DrawRecentBlock = ({ recent }) => (
  <>
    <div className="bdraw-player-meta-row">
      <span className="bdraw-player-meta">{formatDrawPlayedAt(recent.playedAt) || "—"}</span>
      <DrawStatusPill status={recent.status} />
    </div>
    <DrawGameMetrics recent={recent} />
    <div className="bdraw-player-numbers">
      <div className="bdraw-player-number-row">
        <span className="bdraw-player-number-label">Lucky</span>
        <DrawNumberPills numbers={recent.lucky} />
      </div>
      <div className="bdraw-player-number-row">
        <span className="bdraw-player-number-label">Draw</span>
        <DrawNumberPills numbers={recent.draw} />
      </div>
      <div className="bdraw-player-number-row">
        <span className="bdraw-player-number-label">Match</span>
        {recent.matches?.length > 0 ? (
          <DrawNumberPills numbers={recent.matches} match />
        ) : (
          <span className="bdraw-player-meta">—</span>
        )}
      </div>
    </div>
  </>
);

const BondDrawPlayerCard = ({ player, isCurrentUser = false }) => {
  const [expanded, setExpanded] = useState(false);
  const { recent, recentGames = [] } = player;
  const hasRecent = recent && String(recent.status || "").toLowerCase() !== "pending";
  const avatarUrl = generateAvatarUrl(player.name || "Player");

  return (
    <li className={`bdraw-player-card${expanded ? " expanded" : ""}${isCurrentUser ? " mine" : ""}`}>
      <button
        type="button"
        className="bdraw-player-card-toggle"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        <img className="bdraw-player-avatar" src={avatarUrl} alt="" />
        <span className="bdraw-player-head-main">
          <span className="bdraw-player-name-row">
            <strong>{player.name || "Anonymous"}</strong>
            {isCurrentUser ? <span className="bdraw-player-you">YOU</span> : null}
          </span>
        </span>
        <span className="bdraw-panel-row-stats">
          <span className="bdraw-stat won" title="Won">
            <CheckCircle2 size={11} strokeWidth={2.5} />
            {player.won || 0}
          </span>
          <span className="bdraw-stat lost" title="Lost">
            <XCircle size={11} strokeWidth={2.5} />
            {player.lost || 0}
          </span>
          <span className="bdraw-stat pending" title="Pending">
            <Clock3 size={11} strokeWidth={2.5} />
            {player.pending || 0}
          </span>
        </span>
        <span className="bdraw-player-chevron" aria-hidden>
          {expanded ? <ChevronUp size={14} strokeWidth={2.5} /> : <ChevronDown size={14} strokeWidth={2.5} />}
        </span>
      </button>

      {expanded ? (
        <div className="bdraw-player-card-body">
          {hasRecent ? (
            <>
              <DrawRecentBlock recent={recent} />
              {recentGames.length > 0 ? (
                <div className="bdraw-player-history">
                  <span className="bdraw-player-history-title">Recent</span>
                  {recentGames.slice(0, 5).map((game, idx) => (
                    <div key={`rg-${idx}`} className="bdraw-player-history-card">
                      <DrawRecentBlock recent={game} />
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          ) : (
            <p className="bdraw-player-empty-detail">No completed game yet</p>
          )}
        </div>
      ) : null}
    </li>
  );
};

export default BondDrawPlayerCard;
